import { IComponentCategory } from "@kuika/kuika-cl-model";
import _ from "lodash";
import React from "react";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaComponentIcon from "../../../ui-kit/kuika-component-icon/kuika-component-icon";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import "./component-set-categories.scss";

import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
interface CategoryIconChoice {
  name: string;
  url: string;
}

/**
 * The icons a component category can be shown with. The value persisted in `icon`
 * is the NAME, not the url — keep the names byte-identical.
 */
const CATEGORY_ICON_CHOICES: CategoryIconChoice[] = [
  { name: "General", url: "/images/componentIcons/general-32.svg" },
  { name: "Feedback", url: "/images/componentIcons/feedback-32.svg" },
  { name: "DataInput", url: "/images/componentIcons/data-input-32.svg" },
  { name: "DataDisplay", url: "/images/componentIcons/data-display-32.svg" },
  { name: "Layout", url: "/images/componentIcons/layout-32.svg" },
  { name: "Navigation", url: "/images/componentIcons/navigation-32.svg" },
  { name: "Other", url: "/images/componentIcons/other-32.svg" },
  { name: "Tag", url: "/images/componentIcons/tag-32.svg" },
  { name: "Grid", url: "/images/componentIcons/grid-32.svg" },
  { name: "Tabs", url: "/images/componentIcons/tabs-32.svg" },
  { name: "Text", url: "/images/componentIcons/text-32.svg" },
  { name: "Badge", url: "/images/componentIcons/badge-32.svg" },
  { name: "Breadcrumb", url: "/images/componentIcons/breadcrumb-32.svg" },
  { name: "Table", url: "/images/componentIcons/table-32.svg" },
  { name: "Button", url: "/images/componentIcons/button-32.svg" },
  { name: "Drawer", url: "/images/componentIcons/drawer-32.svg" },
  { name: "Gallery", url: "/images/componentIcons/gallery-32.svg" },
  { name: "Calendar", url: "/images/componentIcons/calendar-32.svg" },
  { name: "Checkbox", url: "/images/componentIcons/checkbox-32.svg" },
  { name: "Uploader", url: "/images/componentIcons/uploader-32.svg" },
  { name: "Accordion", url: "/images/componentIcons/accordion-32.svg" },
  { name: "CardView", url: "/images/componentIcons/card-view-32.svg" },
  { name: "ChartBar", url: "/images/componentIcons/chart-bar-32.svg" },
  { name: "TextLine", url: "/images/componentIcons/text-line-32.svg" },
  { name: "Downloader", url: "/images/componentIcons/downloader-32.svg" },
  { name: "ImageView", url: "/images/componentIcons/image-view-32.svg" },
  { name: "Pagination", url: "/images/componentIcons/pagination-32.svg" },
  { name: "Description", url: "/images/componentIcons/description-32.svg" },
  { name: "Notification", url: "/images/componentIcons/notification-32.svg" },
  { name: "RadioButton", url: "/images/componentIcons/radio-button-32.svg" },
  { name: "InputNumeric", url: "/images/componentIcons/input-numeric-32.svg" },
  { name: "MenuHamburger", url: "/images/componentIcons/menu-hamburger-32.svg" },
  { name: "PickerDateTime", url: "/images/componentIcons/picker-date-time-32.svg" },
  { name: "MultiSelect", url: "/images/componentIcons/multi-select-32.svg" },
  { name: "ComboBox", url: "/images/componentIcons/combobox-32.svg" }
];

export interface IComponentSetCategoriesProps {
  value?: IComponentCategory[];
  onChange?: (categories: IComponentCategory[]) => void;
}

/**
 * The category collection editor. It is a controlled custom form control: the
 * parent binds it with `Form.Item name="categories"` and reads `data.categories`
 * back at submit, so every mutation has to go out through `props.onChange`.
 */
export default class ComponentSetCategories extends React.Component<IComponentSetCategoriesProps> {
  getValue = (): IComponentCategory[] => {
    const isEmpty: boolean = _.isEmpty(this.props.value);
    if (this.props.value && isEmpty === false) {
      return this.props.value;
    }
    return [];
  };

  emit = (categories: IComponentCategory[]): void => {
    this.props.onChange?.(categories);
  };

  addCategory = (): void => {
    // A fresh array rather than a push onto props.value: the old editor mutated
    // the value it was handed, so the form store and the control shared one array.
    this.emit([...this.getValue(), { icon: "General", categoryName: "" } as IComponentCategory]);
  };

  deleteCategory = (index: number): void => {
    const categories: IComponentCategory[] = [...this.getValue()];
    categories.splice(index, 1);
    this.emit(categories);
  };

  setCategoryName = (index: number, rawValue: string): void => {
    // The name becomes an object key in the component set, so it stays letters-only.
    const value: string = rawValue.replace(/[^A-Za-z]/gi, "");
    const categories: IComponentCategory[] = this.getValue();
    const isTaken: boolean = categories.some(
      (item: IComponentCategory, itemIndex: number) => itemIndex !== index && item.categoryName === value
    );
    if (isTaken) return;
    this.emit(categories.map((item, itemIndex) => (itemIndex === index ? { ...item, categoryName: value } : item)));
  };

  setIcon = (index: number, icon: string): void => {
    this.emit(this.getValue().map((item, itemIndex) => (itemIndex === index ? { ...item, icon } : item)));
  };

  getIconOptions = (): KuikaSelectboxOption[] => {
    return CATEGORY_ICON_CHOICES.map((choice: CategoryIconChoice): KuikaSelectboxOption => ({
      value: choice.name,
      searchLabel: choice.name,
      label: (
        <span className="kuika-category-editor__option">
          <KuikaComponentIcon className="kuika-category-editor__option-icon" src={choice.url} width={20} height={20} />
          {choice.name}
        </span>
      )
    }));
  };

  render() {
    const categories: IComponentCategory[] = this.getValue();
    return (
      <div className="kuika-category-editor" data-testid="componentCategoryEditor">
        {categories.length === 0 ? (
          <KuikaEmptyState
            svgName="components"
            customName="No categories yet"
            description="Categories group the components designers pick from."
          />
        ) : (
          <div className="kuika-category-editor__rows">
            <div className="kuika-category-editor__head">
              <KuikaSmallText className="kuika-category-editor__head-cell">Icon</KuikaSmallText>
              <KuikaSmallText className="kuika-category-editor__head-cell">Name</KuikaSmallText>
              <span />
            </div>
            {categories.map((category: IComponentCategory, index: number) => (
              <div
                className="kuika-category-editor__row"
                key={`category-${index}`}
                data-testid={`componentCategoryRow-${index}`}
              >
                <KuikaSelectbox
                  data-testid={`componentCategoryIconSelect-${index}`}
                  searchable
                  placeholder="Icon"
                  options={this.getIconOptions()}
                  value={category.icon}
                  onChange={(value) => this.setIcon(index, String(value))}
                />
                <KuikaInput
                  data-testid={`componentCategoryNameInput-${index}`}
                  placeholder="Category name"
                  value={category.categoryName}
                  onChange={(_e, value) => this.setCategoryName(index, String(value))}
                />
                <KuikaTooltip content="Remove this category">
                  <span>
                    <KuikaButton
                      data-testid={`componentCategoryDeleteButton-${index}`}
                      type="tertiary"
                      color="danger"
                      size="sm"
                      isIcon
                      aria-label={`Delete ${category.categoryName || "category"}`}
                      onClick={() => this.deleteCategory(index)}
                    >
                      <KuikaSvg name="trash" width={16} height={16} color="currentColor" />
                    </KuikaButton>
                  </span>
                </KuikaTooltip>
              </div>
            ))}
          </div>
        )}

        <div className="kuika-category-editor__footer">
          <KuikaButton data-testid="componentCategoryAddButton" type="secondary" size="sm" onClick={this.addCategory}>
            Add category
          </KuikaButton>
        </div>
      </div>
    );
  }
}
